import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AiChat.css';

// ── Suggested starter questions ───────────────────────────────────────────────

const SUGGESTIONS = [
  'How does usage-based billing work?',
  'What API endpoints handle invoice management?',
  'How do I ingest a usage event via the API?',
  'What are credits and how are they applied to invoices?',
  'Walk me through what happens at the end of a billing cycle.',
  'What is the difference between a meter and a usage event?',
];

// ── Markdown renderer ─────────────────────────────────────────────────────────

function MsgContent({ text }) {
  const segments = [];
  const codeBlockRe = /```(\w*)\n?([\s\S]*?)```/g;
  let last = 0, m;

  while ((m = codeBlockRe.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: 'text', content: text.slice(last, m.index) });
    segments.push({ type: 'code', lang: m[1], content: m[2].trimEnd() });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', content: text.slice(last) });

  return (
    <>
      {segments.map((seg, si) =>
        seg.type === 'code' ? (
          <pre key={si} className="msg-code-block">
            {seg.lang && <span className="msg-code-lang">{seg.lang}</span>}
            <code>{seg.content}</code>
          </pre>
        ) : (
          <TextSeg key={si} text={seg.content} />
        )
      )}
    </>
  );
}

function TextSeg({ text }) {
  return text.split('\n').map((line, i, arr) => (
    <React.Fragment key={i}>
      <InlineLine line={line} />
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

function InlineLine({ line }) {
  const bullet = line.match(/^([-*•]|\d+\.) (.+)/);
  if (bullet) {
    return (
      <span className="msg-list-item">
        <span className="msg-bullet-marker">
          {/^\d+\./.test(bullet[1]) ? bullet[1] : '•'}
        </span>
        <span><InlineFormat text={bullet[2]} /></span>
      </span>
    );
  }
  const heading = line.match(/^#{1,3} (.+)/);
  if (heading) return <strong className="msg-heading"><InlineFormat text={heading[1]} /></strong>;
  return <InlineFormat text={line} />;
}

function InlineFormat({ text }) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`')  && part.endsWith('`'))  return <code key={i} className="msg-inline-code">{part.slice(1, -1)}</code>;
    return part;
  });
}

// ── SSE stream parser ─────────────────────────────────────────────────────────

async function* parseSSE(response) {
  const reader  = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer    = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();  // keep incomplete trailing line

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try { yield JSON.parse(line.slice(6)); } catch { /* skip malformed */ }
      }
    }
  }
}

// ── Main chat widget ──────────────────────────────────────────────────────────

export default function AiChat() {
  const [isOpen,     setIsOpen]     = useState(false);
  const [messages,   setMessages]   = useState([]);    // local display state
  const [input,      setInput]      = useState('');
  const [streaming,  setStreaming]  = useState(false);
  const [streamText, setStreamText] = useState('');
  const [error,      setError]      = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const accRef         = useRef('');
  const abortRef       = useRef(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 80);
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'a') {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // ── Send message ────────────────────────────────────────────────────────────

  const sendMessage = useCallback(async (text) => {
    const content = text.trim();
    if (!content || streaming) return;

    setError(null);
    setMessages((prev) => [...prev, { id: Date.now(), role: 'user', content }]);
    setInput('');
    setStreaming(true);
    setStreamText('');
    accRef.current = '';

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBase}/api/chat`, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        credentials: 'include',
        body:         JSON.stringify({ message: content }),
        signal:       abort.signal,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Server error ${response.status}`);
      }

      for await (const event of parseSSE(response)) {
        if (event.type === 'delta') {
          accRef.current += event.text;
          setStreamText(accRef.current);
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
        // 'done' — loop ends naturally when the stream closes
      }
      const finalText = accRef.current;
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: finalText },
      ]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setStreaming(false);
      setStreamText('');
      accRef.current  = '';
      abortRef.current = null;
    }
  }, [streaming]);

  // ── Clear conversation ──────────────────────────────────────────────────────

  async function clearConversation() {
    const apiBase = process.env.REACT_APP_API_URL || '';
    try { await fetch(`${apiBase}/api/chat/session`, { method: 'DELETE', credentials: 'include' }); } catch { /* best-effort */ }
    setMessages([]);
    setError(null);
  }

  function stopStreaming() { abortRef.current?.abort(); }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  const showEmpty = messages.length === 0 && !streaming;

  return (
    <>
      {/* ── Floating button ── */}
      <button
        className={`chat-fab${isOpen ? ' chat-fab--open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        title="Ask AI  (⌘⇧A)"
        aria-label="Open AI assistant"
      >
        <span className="chat-fab-icon">{isOpen ? '✕' : '✦'}</span>
      </button>

      {/* ── Panel ── */}
      {isOpen && (
        <div className="chat-panel">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-left">
              <span className="chat-header-icon">✦</span>
              <div>
                <span className="chat-header-title">Ask AI</span>
                <span className="chat-header-sub">Answers questions about this documentation</span>
              </div>
            </div>
            {messages.length > 0 && (
              <button className="chat-icon-btn" onClick={clearConversation} title="Clear conversation">
                ↺
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {showEmpty && (
              <div className="chat-empty">
                <p className="chat-empty-title">What do you want to know?</p>
                <div className="chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} className="chat-suggestion" onClick={() => sendMessage(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`chat-msg chat-msg--${msg.role}`}>
                {msg.role === 'assistant' && <span className="chat-msg-avatar">✦</span>}
                <div className="chat-msg-bubble">
                  {msg.role === 'assistant' ? <MsgContent text={msg.content} /> : msg.content}
                </div>
              </div>
            ))}

            {streaming && (
              <div className="chat-msg chat-msg--assistant">
                <span className="chat-msg-avatar">✦</span>
                <div className="chat-msg-bubble">
                  {streamText
                    ? <MsgContent text={streamText} />
                    : <span className="chat-thinking"><span /><span /><span /></span>
                  }
                </div>
              </div>
            )}

            {error && (
              <div className="chat-error"><span>⚠</span> {error}</div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-row">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder="Ask anything about the billing system…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            {streaming ? (
              <button className="chat-stop-btn" onClick={stopStreaming} title="Stop">◼</button>
            ) : (
              <button
                className="chat-send-btn"
                disabled={!input.trim()}
                onClick={() => sendMessage(input)}
                title="Send (Enter)"
              >
                ↑
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
